import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, FileText } from 'lucide-react';
import { useCondoContext } from '@/features/auth/use-condo-context';
import { useGastoComun } from '@/features/gastos-comunes/api';
import { EstadoBadge } from '@/features/gastos-comunes/estado-badge';
import { BorradorActions } from '@/features/gastos-comunes/borrador-actions';
import { AnularDialog } from '@/features/gastos-comunes/anular-dialog';
import { PageContainer } from '@/components/common/page-container';
import { PageHeader } from '@/components/common/page-header';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { formatClp } from '@/lib/format/currency';
import { formatLongDate, formatPeriodo } from '@/lib/format/date';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

export function GastoComunDetailRoute() {
  const { gastoId } = useParams();
  const { condoId } = useCondoContext();
  const { data, isLoading } = useGastoComun(condoId, gastoId);

  if (isLoading || !data) {
    return (
      <PageContainer width="wide">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="mt-6 h-96" />
      </PageContainer>
    );
  }
  const { gastoComun, cargos } = data;
  const recaudado = cargos.reduce((s, c) => s + c.montoPagado, 0);
  const cobroPct = gastoComun.montoTotal
    ? (recaudado / gastoComun.montoTotal) * 100
    : 0;

  const isBorrador = gastoComun.estado === 'borrador';
  const canAnular =
    gastoComun.estado === 'emitido' ||
    gastoComun.estado === 'parcialmente_pagado';

  return (
    <PageContainer width="wide">
      <Button variant="ghost" asChild size="sm" className="mb-4">
        <Link to={`/app/condominios/${condoId}/gastos-comunes`}>
          <ArrowLeft className="h-4 w-4" />
          Volver
        </Link>
      </Button>

      <PageHeader
        eyebrow="Gasto comun"
        title={formatPeriodo(gastoComun.periodo)}
        subtitle={`Vencimiento ${formatLongDate(gastoComun.fechaVencimiento)}`}
        meta={
          <div className="mt-2">
            <EstadoBadge estado={gastoComun.estado} />
          </div>
        }
        action={
          <div className="flex items-center gap-2">
            {isBorrador && gastoId && (
              <BorradorActions condoId={condoId} gastoId={gastoId} />
            )}
            {canAnular && gastoId && (
              <AnularDialog condoId={condoId} gastoId={gastoId} />
            )}
            {!isBorrador && !canAnular && (
              <Button variant="outline" size="sm">
                <FileText className="h-4 w-4" />
                Exportar
              </Button>
            )}
          </div>
        }
        ruleSpacing="mb-8"
      />

      {!isBorrador && (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <Card label="Monto total emitido" value={formatClp(gastoComun.montoTotal)} />
          <Card label="Recaudado" value={formatClp(recaudado)} accent="moss" />
          <Card
            label="Tasa de cobro"
            value={`${cobroPct.toFixed(1)}%`}
            accent="accent"
          />
        </div>
      )}

      {isBorrador && (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Card label="Monto total estimado" value={formatClp(gastoComun.montoTotal)} />
          <Card
            label="Lineas"
            value={String(gastoComun.lineas.length)}
            accent="accent"
          />
        </div>
      )}

      <h2 className="font-display mt-10 text-2xl font-semibold">Lineas</h2>
      <ul className="mt-4 divide-y divide-[var(--color-hairline)] border-y border-[var(--color-hairline)]">
        {gastoComun.lineas.map((l, i) => (
          <li key={i} className="flex items-center justify-between py-3">
            <div>
              <p className="font-display text-sm font-semibold">{l.concepto}</p>
              <p className="text-[0.6875rem] uppercase tracking-[0.1em] text-[var(--color-ink-muted)]">
                {l.categoria.replace('_', ' ')}
              </p>
            </div>
            <p className="font-display tabular text-sm font-semibold">
              {formatClp(l.monto)}
            </p>
          </li>
        ))}
      </ul>

      {cargos.length > 0 && (
        <>
          <h2 className="font-display mt-10 text-2xl font-semibold">Cargos por unidad</h2>
          <div className="mt-4 overflow-hidden rounded-md border border-[var(--color-hairline)]">
            <Table>
              <TableHeader>
                <TableRow className="bg-[var(--color-paper-dim)]">
                  <TableHead>Unidad</TableHead>
                  <TableHead>Monto base</TableHead>
                  <TableHead>Pagado</TableHead>
                  <TableHead>Estado</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {cargos.map((c) => (
                  <TableRow key={c.id}>
                    <TableCell className="font-display font-semibold">
                      {c.unidadId.slice(-4)}
                    </TableCell>
                    <TableCell className="tabular">{formatClp(c.montoBase)}</TableCell>
                    <TableCell className="tabular">{formatClp(c.montoPagado)}</TableCell>
                    <TableCell>
                      <Badge
                        variant={
                          c.estado === 'pagado'
                            ? 'success'
                            : c.estado === 'vencido'
                              ? 'danger'
                              : c.estado === 'anulado' || c.estado === 'reembolsado'
                                ? 'danger'
                                : 'neutral'
                        }
                      >
                        {c.estado}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </>
      )}
    </PageContainer>
  );
}

function Card({
  label,
  value,
  accent = 'primary',
}: {
  label: string;
  value: string;
  accent?: 'primary' | 'moss' | 'accent';
}) {
  return (
    <div className="relative overflow-hidden rounded-md border border-[var(--color-hairline)] bg-[var(--color-paper)] p-5">
      <div
        aria-hidden
        className={`absolute left-0 right-0 top-0 h-[3px] ${
          accent === 'primary'
            ? 'bg-[var(--color-primary)]'
            : accent === 'moss'
              ? 'bg-[var(--color-moss)]'
              : 'bg-[var(--color-accent)]'
        }`}
      />
      <p className="eyebrow">{label}</p>
      <p className="font-display-tight mt-2 text-3xl font-semibold tabular">{value}</p>
    </div>
  );
}
